import {useMemo} from 'react'
import {Plus} from 'lucide-react'
import {useI18n} from '@/i18n'
import {useDialog} from '@/hooks/useDialog'
import {Stack} from '@/components/shared/Stack'
import {usePageTitle} from '@/hooks/use-page-title'
import {useAuth} from '@/contexts/auth-context'
import {useInboundConnectors} from '@/hooks/use-inbound-connectors'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {InboundConnectorControllerConnectorsEndpoint} from '@/types/endpoint-contracts'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {formatTimestamp} from '@/lib/date-utils'
import {InboundConnectorListResponseFields, InboundConnectorType, InboundConnectorTypeBadge} from '@/types/inbound-connector'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'

import {SortableHeader} from '@/components/ui/sortable-header'
import {Skeleton} from '@/components/ui/skeleton'
import {CreateInboundConnectorDialog} from '@/features/admin/CreateInboundConnectorDialog'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {EntityLink} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Icon} from '@/components/shared/Icon'
import {PageLayout} from '@/components/shared/PageLayout'

/**
 * Inbound Connectors list page — Phase C.4 unified search migration (issue 342.09).
 * Replaces the legacy Select+SearchInput filter trio with a single
 * {@link UnifiedSearchBar} driven by the
 * {@link InboundConnectorControllerConnectorsEndpoint} contract.
 *
 * Spec invariants this component upholds:
 *   - `q` matches only the connector @Searchable fields (displayName +
 *     description). Opaque external-config fields like `baseUrl`,
 *     `queryFilter`, `settings`, secrets, and the cron expression are NOT
 *     searchable per spec §4.1 / issue 332.
 *   - Zero implicit default filters — an empty URL shows every connector.
 *   - One request per submit / chip removal.
 */
export function InboundConnectors() {
    const i18n = useI18n()
    usePageTitle(i18n.inboundConnectors.pageTitle)
    const {hasPermission} = useAuth()
    const canCreate = hasPermission(permissions.inboundConnectors.manage)

    const createDialog = useDialog()

    const {
        page, pageSize, sortBy, sortDir, queryString,
        setSort, setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: InboundConnectorControllerConnectorsEndpoint,
        sortable: true,
        defaultSortBy: InboundConnectorControllerConnectorsEndpoint.sort.DISPLAY_NAME,
        defaultSortDir: SortDir.ASC,
    })

    const {data: connectorsData, isLoading, error} = useInboundConnectors(queryString)
    const connectors = useMemo(() => connectorsData?.content ?? [], [connectorsData])
    const totalItems = connectorsData?.totalElements ?? 0
    const totalPages = connectorsData?.totalPages ?? 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const getTypeLabel = (type: string) => {
        return InboundConnectorTypeBadge.of(type as InboundConnectorType)?.label(i18n) ?? type
    }

    return (
        <>
            <PageLayout
                variant="list"
                icon={CONCEPT_ICON.connectorInbound}
                title={i18n.inboundConnectors.allConnectors}
                count={totalItems}
                description={`${i18n.inboundConnectors.connectorCount({count: totalItems})}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
                error={error}
                data-testid="inbound-connectors-list"
                actions={canCreate ? (
                    <Button onClick={createDialog.open}
                            data-testid="create-inbound-connector-button">
                        <Icon icon={Plus} size="md" className="mr-2"/>
                        {i18n.inboundConnectors.createConnector}
                    </Button>
                ) : undefined}
                filters={
                    <UnifiedSearchBar
                        contract={InboundConnectorControllerConnectorsEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                        placeholder={i18n.inboundConnectors.filterPlaceholder}
                    />
                }
                pagination={totalItems > 0 ? (
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={pageSizeOptions}
                    />
                ) : undefined}
            >
                {isLoading ? (
                    <Stack gap="sm" data-testid="inbound-connectors-skeleton">
                        {Array.from({length: 5}).map((_, idx) => (
                            <Skeleton key={idx} className="h-12 w-full"/>
                        ))}
                    </Stack>
                ) : (
                    <Table data-testid="inbound-connectors-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-name">
                                    <SortableHeader
                                        label={i18n.inboundConnectors.columns.name}
                                        sortKey={InboundConnectorListResponseFields.displayName}
                                        currentSortBy={sortBy}
                                        currentSortDir={sortDir}
                                        onSort={setSort}
                                    />
                                </TableHead>
                                <TableHead data-testid="col-target-system">
                                    <SortableHeader
                                        label={i18n.inboundConnectors.columns.targetSystem}
                                        sortKey={InboundConnectorListResponseFields.targetSystem}
                                        currentSortBy={sortBy}
                                        currentSortDir={sortDir}
                                        onSort={setSort}
                                    />
                                </TableHead>
                                <TableHead data-testid="col-type">{i18n.inboundConnectors.columns.type}</TableHead>
                                <TableHead data-testid="col-status">{i18n.inboundConnectors.columns.status}</TableHead>
                                <TableHead data-testid="col-last-sync">
                                    <SortableHeader
                                        label={i18n.inboundConnectors.columns.lastSync}
                                        sortKey={InboundConnectorListResponseFields.lastSyncAt}
                                        currentSortBy={sortBy}
                                        currentSortDir={sortDir}
                                        onSort={setSort}
                                    />
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {connectors.map((connector) => (
                                <ClickableTableRow
                                    key={connector.id}
                                    to={routes.connectors.inbound.detail(connector.id)}
                                    data-testid={`inbound-connector-row-${connector.id}`}
                                >
                                    <TableCell className="font-medium"
                                             data-testid={`inbound-connector-name-${connector.id}`}>
                                        {connector.displayName}
                                    </TableCell>
                                    <TableCell>
                                        <EntityLink value={connector.targetSystem} stopPropagation/>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline">{getTypeLabel(connector.connectorType)}</Badge>
                                    </TableCell>
                                    <TableCell>
                                        {connector.enabled ? (
                                            <Badge variant={BadgeVariant.SUCCESS}>{i18n.common.enabled}</Badge>
                                        ) : (
                                            <Badge variant={BadgeVariant.WARNING}>{i18n.common.disabled}</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {connector.lastSyncAt
                                            ? <MutedText>{formatTimestamp(connector.lastSyncAt)}</MutedText>
                                            : i18n.inboundConnectors.never}
                                    </TableCell>
                                </ClickableTableRow>
                            ))}
                            {connectors.length === 0 && !isLoading && (
                                <TableEmptyState
                                    colSpan={5}
                                    hasFilters={hasActiveFilters}
                                    matchMessage={i18n.inboundConnectors.noConnectorsMatch}
                                    emptyMessage={i18n.inboundConnectors.noConnectorsFound}
                                />
                            )}
                        </TableBody>
                    </Table>
                )}
            </PageLayout>

            <CreateInboundConnectorDialog
                open={createDialog.isOpen}
                onOpenChange={createDialog.bind.onOpenChange}
            />
        </>
    )
}
